import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';

const P = '/photos/studio/';
const photos = [
  { src: `${P}rhs018.webp`, fr: 'La régie du studio', en: 'The studio control room' },
  { src: `${P}redhouse2015_03.webp`, fr: 'La salle live, le piano à queue et les cabines vitrées', en: 'The live room, grand piano and glass booths' },
  { src: `${P}rhs168.webp`, fr: 'La console vue depuis la cabine', en: 'The console seen from the booth' },
  { src: `${P}rhs167.webp`, fr: 'La batterie dans sa cabine', en: 'The drum booth' },
  { src: `${P}rhs110.webp`, fr: 'Amplis Marshall, Ampeg, Vox et Fender', en: 'Marshall, Ampeg, Vox and Fender amps' },
  { src: `${P}rhs046.webp`, fr: 'Câbles sur le mur de bûches', en: 'Patch cables on the log wall' },
  { src: `${P}1465077095745.webp`, fr: 'La salle live et son piano Yamaha', en: 'The live room and its Yamaha piano' },
  { src: `${P}rhs027.webp`, fr: 'La console de la régie', en: 'The control room console' },
  { src: `${P}rhs100.webp`, fr: 'Wurlitzer et Rhodes', en: 'Wurlitzer and Rhodes' },
  { src: `${P}rhs037.webp`, fr: 'Le rack de périphériques', en: 'The outboard rack' },
  { src: `${P}1465077046917.webp`, fr: 'La salle live face aux cabines', en: 'The live room facing the booths' },
];

const pad = (n) => String(n).padStart(2, '0');
const shadow = 'shadow-[0_30px_70px_-25px_rgba(17,17,17,0.55)]';
const frame = `md:absolute md:border-[6px] md:border-[#F5F1EC] ${shadow}`;

const Tile = ({ i, lang, onOpen, className, imgClass = 'object-cover', eager }) => (
  <motion.button
    type="button"
    data-testid={`studio-photo-${i}`}
    onClick={() => onOpen(i)}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative block overflow-hidden bg-[#111] cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E92A3E] ${className}`}
  >
    <img
      src={photos[i].src}
      alt={photos[i][lang]}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      className={`w-full h-full ${imgClass} transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]`}
    />
  </motion.button>
);

const Lightbox = ({ index, setIndex, lang }) => {
  const total = photos.length;
  const touchX = useRef(null);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [setIndex, total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [setIndex, total]);
  const close = useCallback(() => setIndex(null), [setIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [close, next, prev]);

  useEffect(() => {
    [(index + 1) % total, (index - 1 + total) % total].forEach((j) => { new Image().src = photos[j].src; });
  }, [index, total]);

  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  const navBtn = 'absolute top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 hover:bg-white/5 transition-colors duration-300';

  return (
    <motion.div
      data-testid="studio-lightbox"
      role="dialog"
      aria-modal="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[200] bg-[#0B0B0B]/[0.97] backdrop-blur-md flex items-center justify-center select-none"
      onClick={close}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={onTouchEnd}
    >
      <button data-testid="studio-lightbox-close" aria-label="Close" onClick={close}
        className="absolute top-5 right-5 md:top-8 md:right-8 z-20 text-white/70 hover:text-white transition-colors duration-300">
        <X size={30} strokeWidth={1.5} />
      </button>
      <button data-testid="studio-lightbox-prev" aria-label="Previous" className={`${navBtn} left-3 md:left-8`}
        onClick={(e) => { e.stopPropagation(); prev(); }}>
        <ChevronLeft size={26} strokeWidth={1.5} />
      </button>
      <button data-testid="studio-lightbox-next" aria-label="Next" className={`${navBtn} right-3 md:right-8`}
        onClick={(e) => { e.stopPropagation(); next(); }}>
        <ChevronRight size={26} strokeWidth={1.5} />
      </button>

      <AnimatePresence mode="wait">
        <motion.img
          key={index}
          data-testid="studio-lightbox-image"
          src={photos[index].src}
          alt={photos[index][lang]}
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.985 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-[92vw] md:max-w-[84vw] max-h-[70vh] md:max-h-[76vh] -translate-y-5 object-contain shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)]"
        />
      </AnimatePresence>

      <div className="absolute bottom-6 md:bottom-8 left-0 right-0 flex flex-col items-center gap-2 pointer-events-none">
        <p data-testid="studio-lightbox-caption" className="font-heading italic text-white/80 text-sm md:text-base">{photos[index][lang]}</p>
        <p data-testid="studio-lightbox-counter" className="font-mono text-[11px] tracking-[0.3em] text-white/50">
          {pad(index + 1)} / {pad(total)}
        </p>
      </div>
    </motion.div>
  );
};

export const StudioGallery = () => {
  const { lang } = useLang();
  const [index, setIndex] = useState(null);

  return (
    <div data-testid="studio-gallery" className="mb-16 md:mb-24">
      <div className="relative grid grid-cols-2 gap-3 md:block md:aspect-[16/9.7]">
        <Tile i={0} lang={lang} onOpen={setIndex} eager
          className={`col-span-2 aspect-[5/3] md:absolute md:left-[12%] md:top-0 md:w-[54.5%] md:aspect-[5/3.6] ${shadow}`} />
        <Tile i={1} lang={lang} onOpen={setIndex} imgClass="object-cover object-[22%_50%]"
          className={`aspect-square md:aspect-[3/2] ${frame} md:left-0 md:top-[22%] md:w-[24%] md:z-20`} />
        <Tile i={2} lang={lang} onOpen={setIndex}
          className={`aspect-square ${frame} md:left-[61.3%] md:top-[7%] md:w-[23.8%] md:z-10`} />
        <Tile i={3} lang={lang} onOpen={setIndex}
          className={`aspect-square ${frame} md:left-[81.5%] md:top-[30%] md:w-[18.5%] md:z-20`} />
        <Tile i={4} lang={lang} onOpen={setIndex}
          className={`aspect-square md:aspect-[3/2] ${frame} md:left-[19%] md:top-[58%] md:w-[29.9%] md:z-10`} />
        <Tile i={5} lang={lang} onOpen={setIndex}
          className={`aspect-square ${frame} md:left-[53.4%] md:top-[71%] md:w-[16.7%] md:z-10`} />
        <Tile i={6} lang={lang} onOpen={setIndex} imgClass="object-cover object-[30%_50%]"
          className={`aspect-square ${frame} md:left-[3%] md:top-[64%] md:w-[13%] md:z-10`} />
        <GalleryButton lang={lang} onOpen={() => setIndex(0)} />
      </div>

      <AnimatePresence>
        {index !== null && <Lightbox index={index} setIndex={setIndex} lang={lang} />}
      </AnimatePresence>
    </div>
  );
};

const GalleryButton = ({ lang, onOpen }) => (
      <button
        type="button"
        data-testid="studio-gallery-open-btn"
        onClick={onOpen}
        className="group col-span-2 mt-5 md:mt-0 md:absolute md:right-0 md:-bottom-10 lg:bottom-[8%] inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#111] hover:text-[#E92A3E] transition-colors duration-300"
      >
        <Expand size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:scale-110" />
        <span>{lang === 'fr' ? 'Voir la galerie' : 'View the gallery'}</span>
        <span className="font-mono text-[#888] tracking-[0.2em]">{pad(photos.length)}</span>
        <span className="block h-px w-10 bg-current transition-[width] duration-500 group-hover:w-16" />
      </button>
);
